import React, { ReactNode } from 'react'
import AppTablePagination from '../../common/table/AppTablePagination'
import { RolesListColumn } from '../userManagementConstants'
import { useTranslation } from 'react-i18next'
import ATSearchBox from '../../common/formComponent/ATSearchBox'
import ATTextButton from '../../common/formComponent/ATTextButton'
import { Modal } from 'react-bootstrap'
import ATConfirmBox from '../../common/confirmBox/ATConfirmBox'
import ATModuleMessage from '../../common/message/ATModuleMessage'
import ATLoaderContainer from '../../common/loaderContainer/ATLoaderContainer'
import AddRolePopup from './addRole/AddRolePopup'
import useRoles from '../../../customHooks/usermanagement/useRoles'
import { RolesProps } from '../../../utils/types/roles/RolesTypes'
import { getWidgetMapping } from '../../../utils/authUtil/AuthUtil'
import { PERMISSIONS, WIDGET_TYPE } from '../../common/auth/AuthorizeConstant'
import Authorize from '../../common/auth/Authorize'
import './Roles.css'
import RoleActivityModal from './roleActivity/RoleActivityModal'

const Roles: React.FC<RolesProps> = ({ isMaster }) => {
  const { t } = useTranslation(['translation'])
  const {
    pageNumber,
    limit,
    searchQuery,
    showNewRole,
    showDeleteConfirmation,
    showRecoverConfirmation,
    roleList,
    fetchData,
    handlePageSize,
    handleSearchQuery,
    handleRoleModelOpen,
    handleRoleModelClose,
    onDeleteHandler,
    onEditHandler,
    onDeleteConfirmationCancelHandler,
    onDeleteConfirmationHandler,
    onRecoverHandler,
    onRecoverConfirmationCancelHandler,
    onRecoverConfirmationHandler,
    showRoleActivity,
    selectedRole,
    viewRoleActivity,
  } = useRoles(isMaster)

  const getJSX = (): ReactNode => {
    if (roleList?.data?.data?.length === 0)
      return (
        <ATLoaderContainer>
          <ATModuleMessage />
        </ATLoaderContainer>
      )
    else if (roleList?.isSuccess && roleList?.data?.data?.length !== 0) {
      return (
        <AppTablePagination
          data={roleList?.data ?? null}
          limit={limit}
          handlePageChange={(pageNumber: number) => fetchData(pageNumber)}
          currentPage={pageNumber}
          handlePageSize={(pageSize: number) => handlePageSize(pageSize)}
          columns={RolesListColumn(
            onDeleteHandler,
            onEditHandler,
            isMaster,
            viewRoleActivity,
            !isMaster ? onRecoverHandler : undefined,
            !isMaster ? t('user_management.deleted_tag') : undefined,
          )}
          minHeight="calc(100vh - 322px)"
        />
      )
    }
  }

  return (
    <div className="container-fluid h-100 p-2 d-flex flex-column">
      {/* Tells user which roles are listed */}
      <p className="p-0 m-0 fs-6 text-black">
        {t(
          isMaster
            ? 'user_management.master_role_header'
            : 'user_management.platform_role_header',
        )}
      </p>
      {/* Search and Add New Role Button */}
      <div className="d-flex justify-content-between pb-3 pt-2">
        <div>
          <ATSearchBox
            name="roles_query"
            handleChange={(e) => handleSearchQuery(e)}
            onBlurHandler={() => null}
            searchInput={searchQuery}
            placeholder={t('user_management.search_by_role_name')}
            className="noPadding"
          />
        </div>
        <Authorize
          widgetId={getWidgetMapping(
            isMaster ? PERMISSIONS.ADD_MASTER_ROLE : PERMISSIONS.ADD_ROLE,
          )}
          widgetType={WIDGET_TYPE.BUTTON}
        >
          <ATTextButton
            title={t('user_management.add_new_role')}
            onPressHandler={() => handleRoleModelOpen(true)}
          />
        </Authorize>
      </div>
      {/* Table for Platform Roles list */}
      <div className="row flex-1">
        {
          <Authorize
            widgetId={getWidgetMapping(
              isMaster ? PERMISSIONS.VIEW_MASTER_ROLES : PERMISSIONS.VIEW_ROLES,
            )}
            widgetType={WIDGET_TYPE.VIEW}
          >
            {getJSX()}
          </Authorize>
        }
      </div>
      {/* Modal for Add New Role */}
      <Modal show={showNewRole} className="modal-xl">
        <AddRolePopup onCloseModal={handleRoleModelClose} isMaster={isMaster} />
      </Modal>
      <RoleActivityModal
        show={showRoleActivity}
        roleName={selectedRole?.roleName ?? ''}
        roleCode={selectedRole?.roleCode ?? ''}
        onHide={() => viewRoleActivity(undefined, false)}
      />
      <ATConfirmBox
        showModal={showDeleteConfirmation}
        modalTitle={t('user_management.delete_role')}
        modalMessage={t('user_management.delete_role_confirmation_message')}
        onCancel={onDeleteConfirmationCancelHandler}
        onSave={onDeleteConfirmationHandler}
      />
      {!isMaster && (
        <ATConfirmBox
          showModal={showRecoverConfirmation}
          modalTitle={t('user_management.recover_role')}
          modalMessage={t('user_management.recover_role_confirmation_message')}
          saveLabel={'common.yes'}
          cancelLabel={'common.no'}
          onCancel={onRecoverConfirmationCancelHandler}
          onSave={onRecoverConfirmationHandler}
        />
      )}
    </div>
  )
}

export default Roles
